import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useAuth } from '../../contexts'
import { ALL_CLASSES, ALL_ROLES, HORDE_RACES, CLASS_SPECS, type WowClass } from '../../types'
import { normalizeName } from '../../lib/characterName'
import {
  submitGuildApplication,
  saveApplicationDraft,
  loadApplicationDraft,
  clearApplicationDraft,
  EMPTY_DRAFT,
  type GuildApplicationDraft,
} from '../../hooks/useGuildApplications'

const inputClass =
  'w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:border-blue-500'
const labelClass = 'block text-xs text-gray-400 mb-1'

// A Forever name part: letters, with an apostrophe or hyphen inside.
// The database checks the same thing.
const NAME_PART = /^\p{L}+(['-]\p{L}+)*$/u

function nameError(value: string, label: string): string | null {
  if (value.length < 2 || value.length > 24 || !NAME_PART.test(value)) return `${label} must be 2-24 letters`
  return null
}

function DiscordIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z" />
    </svg>
  )
}

/**
 * The application form. The applicant fills it in, then confirms who they are
 * with Discord; on the way back it is sent and they are signed straight out.
 * It lands in the officers' application queue on the Roster page.
 */
export function ApplyForm() {
  const { user, applicant, loading, startApplicantSignIn, endApplicantSession } = useAuth()

  // Back from a failed send, or from Discord: start from what they typed
  const [draft, setDraft] = useState<GuildApplicationDraft>(() => loadApplicationDraft() ?? EMPTY_DRAFT)
  const [error, setError] = useState<string | null>(null)
  const [redirecting, setRedirecting] = useState(false)
  const [sending, setSending] = useState(false)
  const [sentAs, setSentAs] = useState<{ character: string; discord: string | null } | null>(null)
  const submittedRef = useRef(false)

  const set = <K extends keyof GuildApplicationDraft>(key: K, value: GuildApplicationDraft[K]) =>
    setDraft(d => ({ ...d, [key]: value }))

  // Back from Discord: send it, then drop the session whatever happened
  useEffect(() => {
    if (!applicant || submittedRef.current) return
    submittedRef.current = true
    document.getElementById('apply')?.scrollIntoView()

    const saved = loadApplicationDraft()
    if (!saved) {
      void endApplicantSession()
      return
    }

    setSending(true)
    void (async () => {
      const result = await submitGuildApplication(saved)
      const discord = applicant.discordUsername
      await endApplicantSession()
      setSending(false)
      if (result) {
        setError(result)
        submittedRef.current = false
        return
      }
      clearApplicationDraft()
      setSentAs({ character: `${saved.firstName} ${saved.familyName}`, discord })
    })()
  }, [applicant, endApplicantSession])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    const first = normalizeName(draft.firstName)
    const family = normalizeName(draft.familyName)
    const problem = nameError(first, 'First name') ?? nameError(family, 'Family name')
    if (problem) { setError(problem); return }
    if (!draft.className) { setError('Pick your class'); return }
    if (!draft.race) { setError('Pick your race'); return }
    if (!draft.specName) { setError('Pick your main spec'); return }
    if (!draft.roleName) { setError('Pick your main role'); return }
    // Same spec twice only means something if the role tells them apart
    if (draft.osSpec && draft.osSpec === draft.specName && !draft.osRole) {
      setError('That is your main spec too - pick the role your off-spec plays as')
      return
    }

    saveApplicationDraft({
      ...draft,
      firstName: first,
      familyName: family,
      osRole: draft.osSpec ? draft.osRole : '',
      twowCharacterName: draft.twowCharacterName.trim(),
    })
    setRedirecting(true)
    try {
      await startApplicantSignIn()
    } catch {
      setRedirecting(false)
      setError('Could not reach Discord - try again')
    }
  }

  if (sentAs) {
    return (
      <div className="bg-emerald-900/20 border border-emerald-700/40 rounded-lg p-6 text-center">
        <h3 className="text-lg font-semibold text-emerald-300 mb-2">Application sent</h3>
        <p className="text-gray-300 text-sm">
          Thanks, {sentAs.character}. An officer will read it and contact you on Discord
          {sentAs.discord && <> as <span className="font-medium text-gray-100">{sentAs.discord}</span></>}.
        </p>
      </div>
    )
  }

  if (sending) {
    return (
      <div className="bg-raven-dark border border-gray-800 rounded-lg p-6 text-center text-gray-400">
        Sending your application…
      </div>
    )
  }

  const specs = draft.className ? CLASS_SPECS[draft.className as WowClass] ?? [] : []
  const isMember = !!user
  const busy = redirecting || loading

  return (
    <form onSubmit={handleSubmit} className="bg-raven-dark border border-gray-800 rounded-lg p-5 sm:p-6 space-y-4" noValidate>
      {isMember && (
        <p className="text-sm text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2">
          You are logged in as a member. This form is for people who want to join.
        </p>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="apply-first" className={labelClass}>First name</label>
          <input
            id="apply-first"
            className={inputClass}
            value={draft.firstName}
            onChange={e => set('firstName', e.target.value)}
            maxLength={24}
            autoComplete="off"
            required
          />
        </div>
        <div>
          <label htmlFor="apply-family" className={labelClass}>Family name</label>
          <input
            id="apply-family"
            className={inputClass}
            value={draft.familyName}
            onChange={e => set('familyName', e.target.value)}
            maxLength={24}
            autoComplete="off"
            required
          />
        </div>
        <p className="sm:col-span-2 -mt-2 text-[11px] text-gray-500">Your WoW Forever character, exactly as in game.</p>

        <div>
          <label htmlFor="apply-class" className={labelClass}>Class</label>
          <select
            id="apply-class"
            className={inputClass}
            value={draft.className}
            // Specs belong to a class, so a new class starts them over
            onChange={e => setDraft(d => ({ ...d, className: e.target.value, specName: '', osSpec: '', osRole: '' }))}
            required
          >
            <option value="">Choose…</option>
            {ALL_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="apply-race" className={labelClass}>Race (we are Horde)</label>
          <select id="apply-race" className={inputClass} value={draft.race} onChange={e => set('race', e.target.value)} required>
            <option value="">Choose…</option>
            {HORDE_RACES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>

        <div>
          <label htmlFor="apply-spec" className={labelClass}>Main spec</label>
          <select
            id="apply-spec"
            className={inputClass}
            value={draft.specName}
            onChange={e => set('specName', e.target.value)}
            disabled={!draft.className}
            required
          >
            <option value="">{draft.className ? 'Choose…' : 'Pick a class first'}</option>
            {specs.map(sp => <option key={sp} value={sp}>{sp}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="apply-role" className={labelClass}>Main role</label>
          <select id="apply-role" className={inputClass} value={draft.roleName} onChange={e => set('roleName', e.target.value)} required>
            <option value="">Choose…</option>
            {ALL_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>

        <div>
          <label htmlFor="apply-os" className={labelClass}>Active off-spec (optional)</label>
          <select
            id="apply-os"
            className={inputClass}
            value={draft.osSpec}
            onChange={e => setDraft(d => ({ ...d, osSpec: e.target.value, osRole: e.target.value ? d.osRole : '' }))}
            disabled={!draft.className}
          >
            <option value="">None</option>
            {/* The main spec is still offered: one spec can cover two roles,
                so a Feral tank can have Feral again as a DPS off-spec */}
            {specs.map(sp => <option key={sp} value={sp}>{sp}</option>)}
          </select>
        </div>
        {draft.osSpec && (
          <div>
            <label htmlFor="apply-os-role" className={labelClass}>Active off-spec role</label>
            <select id="apply-os-role" className={inputClass} value={draft.osRole} onChange={e => set('osRole', e.target.value)}>
              <option value="">Not sure</option>
              {ALL_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        )}
      </div>

      <fieldset>
        <legend className={labelClass}>I want to join as</legend>
        <div className="grid grid-cols-2 gap-2">
          {([
            ['raider', 'Raider', 'On the raid roster'],
            ['social', 'Social member', 'Here for the company'],
          ] as const).map(([value, title, hint]) => (
            <label
              key={value}
              className={`cursor-pointer rounded border px-3 py-2 transition-colors ${
                draft.membershipIntent === value ? 'border-blue-500 bg-blue-500/10' : 'border-gray-700 hover:border-gray-500'
              }`}
            >
              <input
                type="radio"
                name="membership"
                value={value}
                checked={draft.membershipIntent === value}
                onChange={() => set('membershipIntent', value)}
                className="sr-only"
              />
              <span className="block text-sm font-medium text-gray-200">{title}</span>
              <span className="block text-xs text-gray-500">{hint}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div>
        <label htmlFor="apply-twow" className={labelClass}>Turtle WoW character name (optional)</label>
        <input
          id="apply-twow"
          className={inputClass}
          value={draft.twowCharacterName}
          onChange={e => set('twowCharacterName', e.target.value)}
          maxLength={48}
          autoComplete="off"
          placeholder="If you played on Turtle WoW"
        />
      </div>

      <div>
        <label htmlFor="apply-about" className={labelClass}>About you (optional)</label>
        <textarea
          id="apply-about"
          className={`${inputClass} min-h-[100px]`}
          value={draft.about}
          onChange={e => set('about', e.target.value)}
          maxLength={1500}
          placeholder="Raiding experience, which raid nights you can make, anything we should know"
        />
      </div>

      {error && <p className="text-sm text-red-400">{error}</p>}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-gray-500 max-w-sm">
          By applying you accept the <a href="#rules" className="text-blue-400 hover:text-blue-300">guild rules</a>.
          Discord confirms who you are so an officer can contact you - you are signed out again
          straight away. Only officers see your application.
        </p>
        <button
          type="submit"
          disabled={busy || isMember}
          className="inline-flex items-center gap-2 bg-[#5865F2] hover:bg-[#4752C4] disabled:opacity-50 disabled:cursor-not-allowed px-5 py-3 rounded-lg text-white font-medium transition-colors"
        >
          <DiscordIcon />
          {redirecting ? 'Opening Discord…' : 'Send with Discord'}
        </button>
      </div>
    </form>
  )
}
